import { useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Check, CreditCard, Lock, Minus } from 'lucide-react';
import { PLAN_MODULES, planCountsTailorSeats, type PlanOption } from '@tailoring-crm/shared';
import { EmptyState } from '../components/feedback/empty-state.js';
import { PageHeader } from '../components/layout/page-header.js';
import { PaymentSection } from '../components/billing/payment-section.js';
import { ResourceMeter } from '../components/billing/resource-meter.js';
import { UsageMeter } from '../components/billing/usage-meter.js';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  useConfirm,
  ListSkeleton,
  useToast,
} from '@tailoring-crm/ui';
import { useAuth } from '../lib/auth/auth-context.js';
import { useBillingState, useChangePlan, useSubscription } from '../lib/billing/use-billing.js';
import { planChangeBlockFrom } from '../lib/billing/plan-refusal.js';
import { formatCurrency, formatDateLong } from '../lib/format.js';

/**
 * The billing screen: what the shop is on, what it is using, and what else it could be on.
 *
 * Three things are deliberately on one screen rather than three. An owner opening this is
 * asking one compound question — "what am I paying, am I close to a limit, and what would
 * more cost?" — and splitting it across tabs turns a thirty-second decision into a hunt.
 *
 * The usage meters sit ABOVE the plan table on purpose. Somebody who arrives here after being
 * refused needs to see how close they were before they see prices; somebody who arrives here
 * curious should learn where they stand before they are sold anything.
 *
 * A downgrade the shop cannot take is shown, disabled, with the reason. Hiding it would leave
 * an owner who wants to spend less staring at a screen that silently disagrees with them.
 */
export function BillingPage(): ReactElement {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { role } = useAuth();
  const isOwner = role === 'owner';

  const confirm = useConfirm();
  const subscription = useSubscription({ enabled: isOwner });
  const billing = useBillingState({ enabled: isOwner });
  const change = useChangePlan();
  const [blocked, setBlocked] = useState<string | null>(null);

  if (!isOwner) {
    // The commercial relationship is the owner's; the API refuses everyone else. Saying so
    // plainly beats rendering an empty screen that looks broken.
    return (
      <>
        <PageHeader title={t('billing.title')} />
        <EmptyState
          icon={Lock}
          title={t('billing.ownerOnlyTitle')}
          description={t('billing.ownerOnlyHint')}
        />
      </>
    );
  }

  if (subscription.isPending) {
    return (
      <>
        <PageHeader title={t('billing.title')} description={t('billing.description')} />
        <ListSkeleton rows={4} />
      </>
    );
  }

  const summary = subscription.data;
  if (!summary) {
    return (
      <>
        <PageHeader title={t('billing.title')} />
        <EmptyState
          icon={AlertTriangle}
          title={t('error.unexpected')}
          description={t('billing.loadFailed')}
        />
      </>
    );
  }

  const currentPrice = summary.plans.find((plan) => plan.current)?.monthlyPricePaise ?? 0;

  async function choose(plan: PlanOption): Promise<void> {
    setBlocked(null);
    // The one question an owner actually has — "what happens to my data?" — asked BEFORE the
    // change, because afterwards the answer arrives too late to be reassuring.
    const ok = await confirm({
      title: t('billing.confirmTitle', { plan: plan.label }),
      description:
        plan.monthlyPricePaise < currentPrice
          ? t('billing.confirmDowngrade')
          : t('billing.confirmUpgrade'),
      confirmLabel: t('billing.confirmAction'),
    });
    if (!ok) return;

    try {
      const result = await change.mutateAsync({ planKey: plan.key });
      toast({ title: t('billing.changed', { plan: result.subscription.planLabel }) });
    } catch (error) {
      // A blocked change is not a failure to report as one: it names exactly what must happen
      // first, and that sentence belongs in front of the button they just pressed.
      const block = planChangeBlockFrom(error);
      setBlocked(
        block
          ? block.reason === 'staff_over_allowance'
            ? t('billing.block.staff', { count: block.excess, allowance: block.allowance })
            : t('billing.block.invites', { count: block.excess, allowance: block.allowance })
          : t('error.unexpected'),
      );
    }
  }

  return (
    <>
      <PageHeader title={t('billing.title')} description={t('billing.description')} />

      <div className="flex flex-col gap-6">
        {/* Where the shop stands, in one line, before anything is sold to them. */}
        <Card>
          <CardHeader className="flex-row flex-wrap items-center justify-between gap-2">
            <CardTitle className="flex flex-wrap items-center gap-2">
              {summary.planLabel}
              <Badge variant={summary.readOnly ? 'destructive' : 'success'}>
                {t(`billing.status.${summary.status}`)}
              </Badge>
            </CardTitle>
            <span className="text-sm text-muted-foreground">
              {t(`billing.cycle.${summary.billingCycle}`)}
            </span>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            <p className="text-sm" role="status">
              {summary.trialDaysRemaining !== null
                ? t('billing.trialRemaining', { count: summary.trialDaysRemaining })
                : summary.graceDaysRemaining !== null
                  ? t('billing.graceRemaining', { count: summary.graceDaysRemaining })
                  : null}
              {summary.renewalDate ? (
                <span className="block text-muted-foreground">
                  {t(summary.status === 'trialing' ? 'billing.trialEnds' : 'billing.renewsOn', {
                    date: formatDateLong(summary.renewalDate),
                  })}
                </span>
              ) : null}
            </p>

            {summary.readOnly ? (
              <p role="alert" className="rounded-md bg-destructive/10 p-3 text-sm">
                {t('billing.readOnlyNotice')}
              </p>
            ) : null}

            <UsageMeter label={t('billing.usage.seats')} usage={summary.usage.seats} />

            {/*
              And the counted allowances beside the seats, from the same response. An owner
              deciding whether the next tier is worth the money is asking exactly one question —
              "what am I close to?" — and answering it needs every meter on one screen, not a
              seat count here and a catalogue figure three navigations away.
            */}
            {summary.usage.resources.map((usage) => (
              <ResourceMeter key={usage.resource} usage={usage} />
            ))}
          </CardContent>
        </Card>

        {/*
          How the shop actually pays (task 24). Below where it stands and ABOVE the plan table,
          because an owner arriving because a payment failed needs the way to fix it before they
          are shown anything else — and one arriving to upgrade scrolls past it in a second.
        */}
        {billing.data ? <PaymentSection state={billing.data} planKey={summary.planKey} /> : null}

        {/* What else they could be on. */}
        <section className="flex flex-col gap-3">
          <h3 className="text-sm font-semibold text-muted-foreground">
            {t('billing.plansHeading')}
          </h3>

          {blocked ? (
            <p role="alert" className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
              {blocked}
            </p>
          ) : null}

          <ul className="grid gap-3 sm:grid-cols-3">
            {summary.plans.map((plan) => (
              <li key={plan.key}>
                <PlanCard
                  plan={plan}
                  pending={change.isPending}
                  onChoose={() => void choose(plan)}
                />
              </li>
            ))}
          </ul>
        </section>
      </div>
    </>
  );
}

function PlanCard({
  plan,
  pending,
  onChoose,
}: {
  plan: PlanOption;
  pending: boolean;
  onChoose: () => void;
}): ReactElement {
  const { t } = useTranslation();

  return (
    <Card className={plan.current ? 'border-primary' : ''}>
      <CardHeader className="gap-2">
        <CardTitle className="flex flex-wrap items-center gap-2">
          {plan.label}
          {plan.current ? <Badge>{t('billing.currentPlan')}</Badge> : null}
        </CardTitle>
        <p className="text-sm">
          {/* Compact, because these are round advertised prices: "₹999.00 per month" reads
              like a calculated figure rather than a price on a page. */}
          <span className="text-lg font-semibold">
            {formatCurrency(plan.monthlyPricePaise, { compact: true })}
          </span>
          <span className="text-muted-foreground"> {t('billing.perMonth')}</span>
        </p>
        <p className="text-sm text-muted-foreground">
          {/* Two months free, stated as the saving rather than as a second price to compare. */}
          {t('billing.annually', {
            price: formatCurrency(plan.annualPricePaise, { compact: true }),
          })}
        </p>
        <p className="text-sm text-muted-foreground">
          {plan.staffSeats === null
            ? t('billing.seatsUnlimited')
            : t(
                planCountsTailorSeats(plan.key)
                  ? 'billing.seatsIncluded'
                  : 'billing.officeSeatsIncluded',
                { count: plan.staffSeats },
              )}
        </p>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        <ul className="flex flex-col gap-1">
          {PLAN_MODULES.filter((module) => module !== 'core').map((module) => {
            const included = plan.modules.includes(module);
            return (
              <li
                key={module}
                className={`flex items-center gap-2 text-sm ${
                  included ? '' : 'text-muted-foreground'
                }`}
              >
                {included ? (
                  <Check aria-hidden="true" className="size-4 shrink-0 text-success" />
                ) : (
                  <Minus aria-hidden="true" className="size-4 shrink-0" />
                )}
                <span>{t(`billing.module.${module}`)}</span>
              </li>
            );
          })}
        </ul>

        {plan.current ? null : plan.available ? (
          <Button block disabled={pending} onClick={onChoose}>
            <CreditCard aria-hidden="true" />
            {t('billing.choose', { plan: plan.label })}
          </Button>
        ) : (
          <>
            <Button block disabled variant="outline">
              {t('billing.choose', { plan: plan.label })}
            </Button>
            <p className="text-sm text-muted-foreground">{plan.blockedReason}</p>
          </>
        )}
      </CardContent>
    </Card>
  );
}

export default BillingPage;
